// Cross-platform inventory sync ("Stage 2" auto-deduct).
//
// The problem this solves: when a part sells on BrickLink, BrickLink reduces
// its OWN stock but BrickOwl never hears about it — so BrickOwl can sell the
// same physical part again, and you oversell. (And vice-versa.)
//
// When an order ships, this module reduces the matching lot on the OTHER
// platform. It is deliberately conservative:
//   * Idempotent — every processed (order, line) is written to the
//     `sync_deductions` ledger, so a lot is never deducted twice even if you
//     re-ship or re-run.
//   * Floored — it never drives a quantity below 0. If the other platform is
//     already short, it floors at 0 and flags it ('floored') instead of
//     writing a negative.
//   * Gated — `sync_mode` in app_settings controls it:
//       'off'     do nothing
//       'preview' compute what WOULD change, write nothing (nothing hits the
//                 live stores, nothing hits the ledger)
//       'live'    actually reduce the other platform and record the ledger
//
// The join key between the two platforms is the BrickLink lot id
// (inventory_id). BrickOwl order lines carry it as `bl_lot_id`; BrickLink
// order lines carry it as `inventory_id`; BrickOwl inventory lots carry it too
// (BrickFreedom links them), which lets us map a BrickLink lot to a BrickOwl
// lot for the reverse direction.

import { supabase } from './supabase';
import { API_BASE } from '../apiBase';

const API = API_BASE;

export async function getSyncMode() {
  try {
    const { data } = await supabase
      .from('app_settings').select('value').eq('key', 'sync_mode').maybeSingle();
    const v = data?.value;
    return (v === 'live' || v === 'off') ? v : 'preview';
  } catch {
    return 'preview';
  }
}

export async function setSyncMode(mode) {
  await supabase.from('app_settings').upsert(
    { key: 'sync_mode', value: mode, updated_at: new Date().toISOString() },
    { onConflict: 'key' }
  );
}

// --- Plan builders --------------------------------------------------------
// A "plan line" = { lineKey, targetLotId, partNo, color, sold, current,
//                   deduct, newQty, note }

// BrickOwl order shipped -> reduce BrickLink. Each BO line's bl_lot_id IS the
// BrickLink inventory_id, so we deduct that lot directly.
async function planBoToBl(boOrderId) {
  const [itemsRes, invRes] = await Promise.all([
    fetch(`${API}/api/brickowl/orders/${boOrderId}/items`).then(r => r.json()),
    fetch(`${API}/api/inventories`).then(r => r.json()),
  ]);
  const items = Array.isArray(itemsRes) ? itemsRes : (itemsRes.items || []);
  const blMap = {};
  (invRes.data || []).forEach(lot => {
    if (lot.inventory_id == null) return;
    blMap[String(lot.inventory_id)] = {
      qty: lot.quantity || 0,
      partNo: lot.item?.no || '',
      color: lot.color_name || '',
    };
  });

  const plan = [];
  for (const it of items) {
    const lotId = it.bl_lot_id;
    const sold = parseInt(it.ordered_quantity || 0, 10) || 0;
    if (lotId == null || sold <= 0) continue;
    const lineKey = String(lotId);
    const lot = blMap[lineKey];
    if (!lot) {
      // BrickLink no longer lists this lot — nothing to reduce.
      plan.push({ lineKey, targetLotId: lineKey, partNo: it.name || '', color: it.color_name || '',
        sold, current: 0, deduct: 0, newQty: 0, note: 'not-on-target' });
      continue;
    }
    const deduct = Math.min(sold, lot.qty);
    plan.push({
      lineKey, targetLotId: lineKey, partNo: lot.partNo, color: lot.color,
      sold, current: lot.qty, deduct, newQty: lot.qty - deduct,
      note: deduct < sold ? 'floored' : 'ok',
    });
  }
  return plan;
}

// Pull the BrickLink lot id out of a BrickOwl inventory lot. BrickFreedom
// stores it, but the exact field name isn't guaranteed, so check the likely
// spots defensively.
function boLotBlId(lot) {
  const cand = lot.bl_lot_id ?? lot.external_id ?? lot.external_lot_id
    ?? lot.external_lot_ids?.bricklink ?? lot.external_lot_ids?.BrickLink;
  return cand == null ? null : String(cand);
}

// BrickLink order shipped -> reduce BrickOwl. Map each BL line's inventory_id
// to a BrickOwl lot via the BrickOwl inventory's stored BrickLink lot id, then
// set that BrickOwl lot to an absolute floored quantity.
async function planBlToBo(blOrderId) {
  const [itemsRes, boInv] = await Promise.all([
    fetch(`${API}/api/orders/${blOrderId}/items`).then(r => r.json()),
    fetch(`${API}/api/brickowl/inventory`).then(r => r.json()),
  ]);
  const batches = itemsRes.data || [];
  const items = batches.flat();

  const boLots = Array.isArray(boInv) ? boInv : (boInv.inventory || boInv.lots || boInv.data || []);
  const boMap = {};
  boLots.forEach(lot => {
    const blId = boLotBlId(lot);
    if (blId == null) return;
    boMap[blId] = {
      lotId: String(lot.lot_id ?? lot.id ?? ''),
      qty: parseInt(lot.quantity ?? lot.qty ?? 0, 10) || 0,
    };
  });

  const plan = [];
  for (const it of items) {
    const invId = it.inventory_id;
    const sold = it.quantity || 0;
    if (invId == null || sold <= 0) continue;
    const lineKey = String(invId);
    const bo = boMap[lineKey];
    const partNo = it.item?.no || '';
    const color = it.color_name || '';
    if (!bo || !bo.lotId) {
      plan.push({ lineKey, targetLotId: null, partNo, color,
        sold, current: 0, deduct: 0, newQty: 0, note: 'not-on-target' });
      continue;
    }
    const deduct = Math.min(sold, bo.qty);
    plan.push({
      lineKey, targetLotId: bo.lotId, partNo, color,
      sold, current: bo.qty, deduct, newQty: bo.qty - deduct,
      note: deduct < sold ? 'floored' : 'ok',
    });
  }
  return plan;
}

// --- Apply ----------------------------------------------------------------
async function applyLine(targetPlatform, p) {
  if (p.note === 'not-on-target' || p.deduct <= 0) {
    return { status: 'skipped', detail: p.note === 'not-on-target' ? 'not listed on other platform' : 'nothing to deduct' };
  }
  try {
    if (targetPlatform === 'bricklink') {
      // BrickLink PUT quantity is a DELTA.
      const r = await fetch(`${API}/api/inventories/${p.targetLotId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity: -p.deduct }),
      });
      const j = await r.json().catch(() => ({}));
      if (j.error || j.meta?.code >= 400) {
        return { status: 'error', detail: j.error || j.meta?.message || `HTTP ${r.status}` };
      }
    } else {
      // BrickOwl takes an ABSOLUTE quantity.
      const r = await fetch(`${API}/api/brickowl/inventory/${p.targetLotId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ absolute_quantity: p.newQty }),
      });
      const j = await r.json().catch(() => ({}));
      if (j.error || j.status === 'error') {
        return { status: 'error', detail: j.error || JSON.stringify(j).slice(0, 120) };
      }
    }
    return { status: p.note === 'floored' ? 'floored' : 'applied', detail: `−${p.deduct} → ${p.newQty}` };
  } catch (err) {
    return { status: 'error', detail: err.message };
  }
}

// --- Public entry point ---------------------------------------------------
// Reduce the OTHER platform for a shipped order. Returns a report the caller
// can surface to the user. Never throws — errors come back per-line.
export async function syncDeductForOrder(order) {
  const mode = await getSyncMode();
  if (mode === 'off') return { mode, lines: [] };

  const sourcePlatform = order.source === 'brickowl' ? 'brickowl' : 'bricklink';
  const targetPlatform = sourcePlatform === 'brickowl' ? 'bricklink' : 'brickowl';
  const sourceOrderId = String(sourcePlatform === 'brickowl' ? order.boId : order.id);

  let plan;
  try {
    plan = sourcePlatform === 'brickowl'
      ? await planBoToBl(sourceOrderId)
      : await planBlToBo(sourceOrderId);
  } catch (err) {
    return { mode, sourcePlatform, targetPlatform, error: err.message, lines: [] };
  }

  // Idempotency: skip line keys already settled for this order.
  const { data: prior } = await supabase
    .from('sync_deductions')
    .select('line_key,status')
    .eq('source_platform', sourcePlatform)
    .eq('source_order_id', sourceOrderId);
  const settled = new Set(
    (prior || [])
      .filter(r => ['applied', 'floored', 'skipped'].includes(r.status))
      .map(r => r.line_key)
  );

  const lines = [];
  for (const p of plan) {
    if (settled.has(p.lineKey)) { lines.push({ ...p, status: 'already' }); continue; }
    if (mode === 'preview') { lines.push({ ...p, status: 'preview' }); continue; }

    const applied = await applyLine(targetPlatform, p);
    lines.push({ ...p, status: applied.status, detail: applied.detail });

    // Only record real deductions. 'error' stays unrecorded so it retries;
    // 'skipped' (nothing to deduct / not on other store) is harmless to redo,
    // so we don't lock it in the ledger either.
    if (applied.status === 'applied' || applied.status === 'floored') {
      await supabase.from('sync_deductions').insert({
        source_platform: sourcePlatform, source_order_id: sourceOrderId,
        line_key: p.lineKey, target_platform: targetPlatform, target_lot_id: p.targetLotId,
        part_no: p.partNo, color: p.color, qty: p.deduct, status: applied.status, detail: applied.detail,
      }).then(() => {}, () => {});
    }
  }

  return { mode, sourcePlatform, targetPlatform, lines };
}
